import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { pcRoutes } from '@/pc/router';
import { mobileRoutes } from '@/m/router';
import { isMobileUA, isWechatUA } from '@/utils/platform';

export function pcHomeRoute(roleCodes: string[], isSuperAdminMode = false) {
  if (isSuperAdminMode) return { name: 'super-admin' };
  return { name: 'dashboard' };
}

export function mobileHomeRoute(isSuperAdminMode = false) {
  if (isSuperAdminMode) return { name: 'm-super-admin' };
  return { name: 'm-dashboard' };
}

const routes = [
  ...mobileRoutes,
  ...pcRoutes,
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!auth.isLogin && !auth.fetchMeAttempted) {
    auth.fetchMeAttempted = true;
    await auth.fetchMe();
  }

  if (to.path === '/') {
    if (!auth.isLogin) {
      if (isWechatUA() || isMobileUA()) return { name: 'm-login' };
      return true;
    }
    if (isMobileUA()) return mobileHomeRoute(auth.isSuperAdminMode);
    return pcHomeRoute(auth.roleCodes, auth.isSuperAdminMode);
  }

  if (to.meta.public) {
    if (auth.isLogin && (to.name === 'login' || to.name === 'm-login')) {
      if (isMobileUA()) return mobileHomeRoute(auth.isSuperAdminMode);
      return pcHomeRoute(auth.roleCodes, auth.isSuperAdminMode);
    }
    return true;
  }

  if (!auth.isLogin) {
    if (to.path.startsWith('/m')) return { name: 'm-login', query: { redirect: to.fullPath } };
    if (isWechatUA() && to.path.startsWith('/pc')) return { name: 'm-login', query: { redirect: to.fullPath } };
    if (to.path.startsWith('/pc')) return { name: 'login', query: { redirect: to.fullPath } };
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const permCodes = to.meta.permissions as string[] | undefined;
  if (permCodes && permCodes.length > 0 && !auth.hasAnyPermission(...permCodes)) {
    if (to.path.startsWith('/m')) return mobileHomeRoute(auth.isSuperAdminMode);
    return pcHomeRoute(auth.roleCodes, auth.isSuperAdminMode);
  }

  const roles = to.meta.roles as string[] | undefined;
  if (roles && roles.length > 0) {
    if (roles.includes('super_admin')) {
      if (!auth.isSuperAdminMode) {
        if (to.path.startsWith('/m')) return mobileHomeRoute(auth.isSuperAdminMode);
        return pcHomeRoute(auth.roleCodes, auth.isSuperAdminMode);
      }
    } else if (!auth.hasAnyRole(...roles)) {
      if (to.path.startsWith('/m')) return mobileHomeRoute(auth.isSuperAdminMode);
      return pcHomeRoute(auth.roleCodes, auth.isSuperAdminMode);
    }
  }

  return true;
});

export default router;
